import React, { useRef } from 'react';
import { Card, SectionHeader } from '@/components/Card';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { MediaUpload } from '@/components/MediaUpload';
import { RichBodyEditor, RichBodyEditorHandle, VariableInsertButton } from './RichBodyEditor';
import { HeaderType, TemplateLocation } from '@/services/templates.service';
import { Image as ImageIcon, Type, Film, FileText, MapPin, Slash, Heading } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  type: HeaderType;
  text?: string;
  imageUrl?: string;
  videoUrl?: string;
  documentUrl?: string;
  documentFilename?: string;
  location?: TemplateLocation;
  onChange: (patch: Partial<{
    headerType: HeaderType;
    headerText: string;
    headerImageUrl: string;
    headerVideoUrl: string;
    headerDocumentUrl: string;
    headerDocumentFilename: string;
    headerLocation: TemplateLocation;
  }>) => void;
  /** Returns names used elsewhere (e.g. body) so the header's parameter counter stays unique. */
  externalUsedNames?: () => string[];
}

const TYPES: { value: HeaderType; label: string; icon: React.ComponentType<{ size?: number }>; hint: string }[] = [
  { value: 'NONE',     label: 'None',     icon: Slash,   hint: 'No header' },
  { value: 'TEXT',     label: 'Text',     icon: Type,    hint: 'Up to 60 chars, supports variables' },
  { value: 'IMAGE',    label: 'Image',    icon: ImageIcon, hint: 'JPG, PNG, WEBP' },
  { value: 'VIDEO',    label: 'Video',    icon: Film,    hint: 'MP4, 3GP' },
  { value: 'DOCUMENT', label: 'Document', icon: FileText, hint: 'PDF, DOCX, XLSX' },
  { value: 'LOCATION', label: 'Location', icon: MapPin,  hint: 'Latitude/longitude' },
];

export const HeaderEditor: React.FC<Props> = (props) => {
  const { type, text, imageUrl, videoUrl, documentUrl, documentFilename, location, onChange, externalUsedNames } = props;
  const richRef = useRef<RichBodyEditorHandle>(null);

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <SectionHeader
          title="Header"
          subtitle="Optional. Choose a media or text header."
          icon={<Heading size={15} />}
          className="mb-0"
        />
        {type === 'TEXT' && (
          <VariableInsertButton onClick={() => richRef.current?.insertNextParameter()} />
        )}
      </div>

      <div className="px-5 py-4 space-y-3">
        {/* Type tiles */}
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {TYPES.map((t) => {
            const Icon = t.icon;
            const active = type === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => onChange({ headerType: t.value })}
                className={clsx(
                  'group flex flex-col items-center gap-1.5 rounded-xl border px-3 py-3 text-center transition-all',
                  active
                    ? 'border-primary bg-primary-soft text-primary shadow-soft'
                    : 'border-border bg-surface text-text-secondary hover:border-primary hover:bg-surface-alt',
                )}
                title={t.hint}
              >
                <Icon size={16} />
                <span className="text-xs font-semibold">{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* Per-type editors */}
        {type === 'TEXT' && (
          <div>
            <FormField label="Header text" hint={`${(text ?? '').length} / 60`}>
              <RichBodyEditor
                ref={richRef}
                value={text ?? ''}
                onChange={(v) => onChange({ headerText: v })}
                externalUsedNames={externalUsedNames}
                maxLength={60}
                rows={1}
                placeholder="Order #{{orderId}}"
              />
            </FormField>
          </div>
        )}
        {type === 'IMAGE' && (
          <MediaUpload
            mode="image"
            value={imageUrl}
            onChange={(url) => onChange({ headerImageUrl: url })}
            folder="whatsapp/template/images"
          />
        )}
        {type === 'VIDEO' && (
          <MediaUpload
            mode="video"
            value={videoUrl}
            onChange={(url) => onChange({ headerVideoUrl: url })}
            folder="whatsapp/template/videos"
          />
        )}
        {type === 'DOCUMENT' && (
          <MediaUpload
            mode="document"
            value={documentUrl}
            filename={documentFilename}
            onChange={(url, meta) => onChange({ headerDocumentUrl: url, headerDocumentFilename: meta?.name ?? documentFilename ?? '' })}
            onFilenameChange={(name) => onChange({ headerDocumentFilename: name })}
            folder="whatsapp/template/documents"
          />
        )}
        {type === 'LOCATION' && (
          <div className="grid grid-cols-2 gap-2.5">
            <FormField label="Latitude" required>
              <Input
                type="number" step="any"
                value={location?.latitude ?? ''}
                onChange={(e) => onChange({ headerLocation: { ...(location ?? {}), latitude: parseFloat(e.target.value) || undefined } })}
                placeholder="25.2048"
              />
            </FormField>
            <FormField label="Longitude" required>
              <Input
                type="number" step="any"
                value={location?.longitude ?? ''}
                onChange={(e) => onChange({ headerLocation: { ...(location ?? {}), longitude: parseFloat(e.target.value) || undefined } })}
                placeholder="55.2708"
              />
            </FormField>
            <FormField label="Place name" hint="Optional">
              <Input
                value={location?.name ?? ''}
                onChange={(e) => onChange({ headerLocation: { ...(location ?? {}), name: e.target.value } })}
                placeholder="Burj Khalifa"
              />
            </FormField>
            <FormField label="Address" hint="Optional">
              <Input
                value={location?.address ?? ''}
                onChange={(e) => onChange({ headerLocation: { ...(location ?? {}), address: e.target.value } })}
                placeholder="Downtown Dubai, UAE"
              />
            </FormField>
          </div>
        )}
      </div>
    </Card>
  );
};
